// Oracle PL/SQL procedural local-variable completion helpers (ROUND2-C1.2).
//
// Detects when the cursor is inside an Oracle PL/SQL procedural body:
//   - `CREATE [OR REPLACE] PROCEDURE / FUNCTION / PACKAGE BODY / TRIGGER … IS|AS … BEGIN … END;`
//   - Anonymous block: `DECLARE … BEGIN … END;`
//
// Emits `CompletionCandidate[]` for the variable declarations visible at the
// cursor via the PL/SQL grammar's `parseProceduralBody` + `resolveScopePlSql`.
//
// Tier mapping:
//   - Declared locals and constants → tier 1 (current-statement scope)
//
// Scope gaps (documented, not blocking — each deserves its own capability
// slice):
//   - Cursor variables and cursor attribute refs (`%FOUND`, `%NOTFOUND`, etc.)
//     are not surfaced here; they ship with dedicated cursor-attribute slices.
//   - `%TYPE` / `%ROWTYPE` attribute references produce a candidate whose
//     `detail` string shows the verbatim `typeText` (e.g. `employees%ROWTYPE`)
//     rather than the expanded field list.
//   - Package-member references, FOR-loop iterators, trigger pseudo-rows
//     (`NEW` / `OLD`), and exception names are out-of-scope for this slice.
//
// Bundle-not-loaded guard: `parseProceduralBody` uses a dynamic import of the
// vendored ANTLR-generated PL/SQL parser. Wrapped in try/catch to degrade
// gracefully when the bundle hasn't finished loading yet.

import type { CompletionCandidate } from '@/core/engines';
import { loadCompletionModule } from '@/features/intellisense/telemetry/providerCost';
import { tokenize, type Token } from '../context/tokenizer';
import { scopedProceduralParse } from './scopedProceduralParse';

const ENGINE = 'oracle' as const;

// ─── Procedural-body detection ────────────────────────────────────────────────

/**
 * Returns `true` when the cursor offset lies inside an Oracle PL/SQL
 * procedural body.
 *
 * Implementation: linear token scan from buffer start to the cursor,
 * tracking the most recent `CREATE [OR REPLACE] PROCEDURE|FUNCTION|PACKAGE
 * BODY|TRIGGER` header followed by the `IS` or `AS` keyword (which
 * introduces the declaration section). Once `IS|AS` is seen at the top
 * level (after a `PROCEDURE`/`FUNCTION`/`PACKAGE BODY`/`TRIGGER` header),
 * the cursor is considered inside the body until the next `;` that follows
 * a top-level `END` token, or until the next top-level `CREATE` (indicating
 * a new DDL statement).
 *
 * Anonymous blocks start with the bare `DECLARE` keyword (no `CREATE`
 * header). A bare `DECLARE` at the top of the token stream also sets the
 * body-active flag; the body ends at the semicolon following the matching
 * `END`.
 *
 * The scan is conservative: nested `BEGIN … END` blocks inside the body do
 * NOT clear the body-active flag — they track a nesting depth counter
 * instead.
 */
export function isInsideOracleProceduralBody(
  source: string,
  offset: number,
): boolean {
  const tokens = tokenize(source, 0, offset, ENGINE);
  let bodyActive = false;
  let beginDepth = 0;
  // For a single-BEGIN body (PROCEDURE / FUNCTION / TRIGGER / anonymous
  // DECLARE block) the body is opened by `IS|AS` (or `DECLARE`) and its FIRST
  // top-level `BEGIN` is the body's own opener — it must NOT increment
  // beginDepth, otherwise the matching outer `END` only decrements depth back
  // to 0 instead of clearing `bodyActive`, and the body's locals leak into the
  // following statement. `bodyBeginSeen` marks once that opener is consumed so
  // subsequent (nested) BEGINs are counted normally. PACKAGE bodies have no
  // single mandatory body BEGIN, so this is left off for them (`singleBegin`).
  let singleBeginBody = false;
  let bodyBeginSeen = false;

  for (let i = 0; i < tokens.length; i += 1) {
    const tok = tokens[i];
    if (!tok || tok.start >= offset) break;

    const upper = identUpper(tok);
    if (!upper) continue;

    if (bodyActive) {
      if (
        upper === 'BEGIN' &&
        singleBeginBody &&
        !bodyBeginSeen
      ) {
        // The single-BEGIN body's own opener — establishes the depth-0
        // baseline; the matching outer bare END (at depth 0) clears the body.
        bodyBeginSeen = true;
        continue;
      }
      // Oracle block-scope openers that pair with a matching END:
      //   BEGIN … END          (bare END)
      //   CASE … END CASE      (END CASE)
      //   IF … END IF          (END IF)
      //   LOOP … END LOOP      (END LOOP)
      //
      // F12: `WHILE` and `FOR` are loop HEADERS, not separate block openers —
      // Oracle has NO `END WHILE` / `END FOR`. Both `WHILE cond LOOP … END
      // LOOP` and `FOR … LOOP … END LOOP` open the body with `LOOP` and close
      // it with a single `END LOOP`. Counting `WHILE`/`FOR` as openers
      // produced a permanent +1 (WHILE +1, LOOP +1, but only one `END LOOP`
      // decrement) so `bodyActive` never cleared and the routine's locals
      // leaked into the following statement. The loop body's opener/closer is
      // `LOOP` / `END LOOP`, already covered below.
      if (
        upper === 'BEGIN' ||
        upper === 'CASE' ||
        upper === 'IF' ||
        upper === 'LOOP'
      ) {
        beginDepth += 1;
        continue;
      }
      if (upper === 'END') {
        // Peek at the next token. If it is a compound-statement modifier
        // keyword (IF / LOOP / CASE) AND that token lies before the cursor,
        // the `END <mod>` pair closes the corresponding opener — consume both
        // tokens together and decrement depth for the opener. Otherwise treat
        // the `END` as a bare closer for a `BEGIN`.
        // Mirrors the MySQL helper's `isCompoundClose` lookahead for consistency.
        const nextTok = tokens[i + 1];
        const nextUpper =
          nextTok && nextTok.start < offset ? identUpper(nextTok) : null;
        const isCompoundClose =
          nextUpper === 'IF' ||
          nextUpper === 'LOOP' ||
          nextUpper === 'CASE';
        if (isCompoundClose) {
          // Advance past the modifier token so it isn't re-processed as an
          // opener on the next iteration.
          i += 1;
        }
        if (beginDepth > 0) {
          beginDepth -= 1;
        } else {
          // Top-level END (bare) — closes the current procedural body.
          bodyActive = false;
          beginDepth = 0;
        }
        continue;
      }
      if (upper === 'CREATE' || upper === 'ALTER') {
        // A new DDL statement resets the body unconditionally.
        bodyActive = false;
        beginDepth = 0;
        singleBeginBody = false;
        bodyBeginSeen = false;
        // Fall through to re-evaluate whether this CREATE starts a new body.
      }
    }

    if (!bodyActive) {
      if (upper === 'DECLARE') {
        // Anonymous block: DECLARE … BEGIN … END;
        bodyActive = true;
        beginDepth = 0;
        singleBeginBody = true;
        bodyBeginSeen = false;
        continue;
      }

      if (upper === 'CREATE' || upper === 'ALTER') {
        // Look ahead past optional `OR REPLACE` preamble.
        const afterPreamble = skipCreateOrReplacePreamble(tokens, i + 1);
        const kindTok = tokens[afterPreamble];
        const kindUpper = identUpper(kindTok);

        if (
          kindUpper === 'PROCEDURE' ||
          kindUpper === 'FUNCTION' ||
          kindUpper === 'TRIGGER'
        ) {
          // Scan forward to find IS or AS, which introduces the declaration section.
          const isAsIdx = findNextIsOrAsIdx(tokens, afterPreamble + 1);
          if (isAsIdx !== -1 && tokens[isAsIdx]!.start < offset) {
            bodyActive = true;
            beginDepth = 0;
            // PROCEDURE / FUNCTION / TRIGGER bodies have a single mandatory
            // body BEGIN whose matching outer END closes the routine.
            singleBeginBody = true;
            bodyBeginSeen = false;
            i = isAsIdx;
          }
          continue;
        }

        if (kindUpper === 'PACKAGE') {
          // `CREATE [OR REPLACE] PACKAGE BODY name IS … END;`
          const nextTok = tokens[afterPreamble + 1];
          if (identUpper(nextTok) === 'BODY') {
            const isAsIdx = findNextIsOrAsIdx(tokens, afterPreamble + 2);
            if (isAsIdx !== -1 && tokens[isAsIdx]!.start < offset) {
              bodyActive = true;
              beginDepth = 0;
              // Package bodies have no single mandatory body BEGIN (member
              // subprograms each carry their own BEGIN/END); keep the prior
              // depth-only model for them.
              singleBeginBody = false;
              bodyBeginSeen = false;
              i = isAsIdx;
            }
          }
          continue;
        }
      }
    }
  }

  return bodyActive;
}

// ─── Candidate emission ───────────────────────────────────────────────────────

/**
 * Emit `CompletionCandidate[]` for the variable declarations visible at
 * `offset` inside an Oracle PL/SQL procedural body.
 *
 * Calls `parseProceduralBody` (async, ANTLR-backed) from the
 * `sql-grammar-plsql` grammar package, then `resolveScopePlSql` to obtain
 * the in-scope variable names. Wrapped in try/catch so a missing bundle
 * produces an empty list without aborting the full candidate build.
 */
export async function emitOracleProceduralLocalCandidates(
  source: string,
  offset: number,
  out: CompletionCandidate[],
): Promise<void> {
  const before = out.length;
  await emitViaGrammar(source, offset, out);
  // The grammar path degrades when the body does not parse — the normal state
  // while it is being written — and it degrades PARTIALLY: the signature
  // extractor can still yield parameters while `resolveScopePlSql` yields no
  // variables. So the text scan always runs and fills the gaps by name, rather
  // than only when the grammar produced nothing at all.
  const emitted = new Set(
    out.slice(before).map((c) => c.label.toLowerCase()),
  );
  for (const decl of scanOracleDeclaredNames(source, offset)) {
    if (emitted.has(decl.name.toLowerCase())) continue;
    emitted.add(decl.name.toLowerCase());
    out.push({
      label: decl.name,
      insertText: decl.name,
      isSnippet: false,
      kind: 'variable',
      tier: 1,
      detail: decl.detail,
      filterText: decl.name,
    });
  }
}

async function emitViaGrammar(
  source: string,
  offset: number,
  out: CompletionCandidate[],
): Promise<void> {
  try {
    const { parseProceduralBody } = await loadCompletionModule(
      '@/features/sql-grammar-plsql/parse',
      () => import('@/features/sql-grammar-plsql/parse'),
    );
    const { resolveScopePlSql } = await loadCompletionModule(
      '@/features/sql-grammar-plsql/resolver/resolveScopePlSql',
      () => import('@/features/sql-grammar-plsql/resolver/resolveScopePlSql'),
    );

    // Statement/block-scoped parse (plan16 T4). Oracle CANNOT reliably slice a
    // single CREATE PROCEDURE/PACKAGE statement: its declaration section
    // (`… IS  v NUMBER := 0;  BEGIN …`) carries top-level `;` terminators that
    // sit OUTSIDE any BEGIN…END block, so `scanDocument` (even with
    // compoundBlockAware) fragments the routine at the first declaration `;`.
    // `oracleStatementSlice` therefore returns null → the helper parses the
    // FULL source, giving `sliceStart = 0` and identity offset translation
    // (`offset - 0`). Oracle still gains the shared cache (repeat completions on
    // an unchanged buffer skip re-parsing) and the D6 oversize guard (a buffer
    // past MAX_MAIN_THREAD_SLICE_BYTES skips procedural locals instead of
    // freezing the main thread). A real oracle statement/`/`-batch extractor is
    // documented follow-up (see plan16 docs-delta).
    const parseResult = await scopedProceduralParse({
      dialect: 'plsql',
      source,
      offset,
      sliceFor: oracleStatementSlice,
      parse: (sliceText) => parseProceduralBody(sliceText, { engine: 'oracle' }),
    });
    if (!parseResult) return; // oversize — degrade to no procedural locals.
    const parsed = parseResult.body;
    // Offset translation (T4 crux): with the full-source fallback this is the
    // absolute offset, but writing `offset - sliceStart` keeps every downstream
    // `visibleAt` comparison correct if oracle ever gains real slicing.
    const scopeOffset = offset - parseResult.sliceStart;
    const scope = resolveScopePlSql(parsed, scopeOffset);

    // Subprogram formal parameters (CB4) — PL/SQL parameters precede the
    // IS / AS body introducer and are NOT in `scope.variables` (which only
    // covers declarations between AS/IS and BEGIN). Surface the parameters of
    // the subprogram that lexically encloses the cursor. PL/SQL subprograms
    // DO nest (package bodies hold member procedures), so the enclosing
    // routine is the one whose signature STARTS latest at or before the
    // cursor — the innermost. The caller already confirmed the cursor is
    // inside a procedural body.
    const { extractSubprogramSignatures } = await loadCompletionModule(
      '@/features/sql-grammar-plsql/extractors/subprogramSignatures',
      () => import('@/features/sql-grammar-plsql/extractors/subprogramSignatures'),
    );
    const enclosing = pickEnclosingSubprogramParams(
      scopeOffset,
      extractSubprogramSignatures(parsed),
    );
    const seenParams = new Set<string>();
    for (const p of enclosing) {
      const key = p.name.toLowerCase();
      if (seenParams.has(key)) continue;
      seenParams.add(key);
      const modeText = p.modes.length > 0 ? p.modes.join(' ') : 'in';
      out.push({
        label: p.name,
        insertText: p.name,
        isSnippet: false,
        kind: 'variable',
        tier: 1,
        detail: `${p.typeText} (${modeText} parameter)`,
        filterText: p.name,
      });
    }

    for (const v of scope.variables) {
      out.push({
        label: v.name,
        insertText: v.name,
        isSnippet: false,
        kind: 'variable',
        tier: 1,
        detail: `${v.typeText} (local variable)`,
        filterText: v.name,
      });
    }

    // Cursor declarations — `CURSOR <name> IS …` and `CURSOR <name>(params)
    // IS …`. These are visible at expression positions inside the enclosing
    // PL/SQL body (the cursor name is referenced in `OPEN c`, `FETCH c INTO
    // …`, `FOR rec IN c LOOP`, etc.). The PL/SQL grammar package already
    // extracts them via `extractCursorDecls`; surfacing here closes oracle
    // gaps script.16 §3 (`c_emps` in FOR-loop iterable position) and
    // script.37 §3 (`c_employees` referenced later in the body).
    const { extractCursorDecls } = await loadCompletionModule(
      '@/features/sql-grammar-plsql/extractors/cursorsOpened',
      () => import('@/features/sql-grammar-plsql/extractors/cursorsOpened'),
    );
    const seenCursors = new Set<string>();
    for (const c of extractCursorDecls(parsed)) {
      if (scopeOffset < c.visibleAt.startOffset || scopeOffset > c.visibleAt.endOffset) {
        continue;
      }
      const key = c.name.toLowerCase();
      if (seenCursors.has(key)) continue;
      seenCursors.add(key);
      out.push({
        label: c.name,
        insertText: c.name,
        isSnippet: false,
        kind: 'variable',
        tier: 1,
        detail: 'cursor',
        filterText: c.name,
      });
    }

    // FOR-loop / FORALL iteration variables — all three forms (cursor FOR
    // `FOR rec IN c LOOP`, numeric-range FOR `FOR i IN 1..10 LOOP`, and
    // PL/SQL-only `FORALL i IN 1..arr.COUNT <stmt>`) introduce a body-scoped
    // identifier. Visible while the cursor sits inside the loop / FORALL
    // body. Closes oracle gaps script.16 §3 (`rec`) and script.42 §7 (`i`).
    const { extractForLoopIterVars } = await loadCompletionModule(
      '@/features/sql-grammar-plsql/extractors/forLoopIterVars',
      () => import('@/features/sql-grammar-plsql/extractors/forLoopIterVars'),
    );
    const seenLoopVars = new Set<string>();
    for (const v of extractForLoopIterVars(parsed)) {
      if (scopeOffset < v.visibleAt.startOffset || scopeOffset > v.visibleAt.endOffset) {
        continue;
      }
      const key = v.name.toLowerCase();
      if (seenLoopVars.has(key)) continue;
      seenLoopVars.add(key);
      out.push({
        label: v.name,
        insertText: v.name,
        isSnippet: false,
        kind: 'variable',
        tier: 1,
        detail: 'FOR-loop iter',
        filterText: v.name,
      });
    }

    // Locally-declared exceptions — `<name> EXCEPTION;` in a DECLARE section.
    // These are referenced both in `RAISE <name>;` and in the handler slot
    // `WHEN <name> THEN`. Surfacing them here (scope-filtered by `visibleAt`)
    // means they appear alongside the predefined exception catalogue at the
    // `WHEN` slot and at RAISE positions. Closes the orchestrator capability
    // "exception name in EXCEPTION handler surfaces locally-declared names".
    const { extractExceptionDecls } = await loadCompletionModule(
      '@/features/sql-grammar-plsql/emitter/exception',
      () => import('@/features/sql-grammar-plsql/emitter/exception'),
    );
    const { buildLineIndex } = await loadCompletionModule(
      '@/features/sql-grammar/document/lineIndex',
      () => import('@/features/sql-grammar/document/lineIndex'),
    );
    const seenExceptions = new Set<string>();
    for (const e of extractExceptionDecls(
      parsed.parseTree,
      parsed.source,
      buildLineIndex(parsed.source),
    )) {
      if (scopeOffset < e.visibleAt.startOffset || scopeOffset > e.visibleAt.endOffset) {
        continue;
      }
      const key = e.name.toLowerCase();
      if (seenExceptions.has(key)) continue;
      seenExceptions.add(key);
      out.push({
        label: e.name,
        insertText: e.name,
        isSnippet: false,
        kind: 'variable',
        tier: 1,
        detail: 'exception (local)',
        filterText: e.name,
      });
    }
  } catch {
    // Bundle not yet loaded or parse failure — degrade gracefully.
    // The DML-side candidates (tables, columns, keywords) still flow.
  }
}

// ─── Slice derivation (plan16 T4) ────────────────────────────────────────────

/**
 * Slice strategy for oracle procedural locals: ALWAYS full source (returns
 * null, ignoring position — hence the no-arg signature, still assignable to the
 * shared `sliceFor` contract). Oracle's `IS … <decls>; BEGIN … END;`
 * declaration section carries top-level `;` terminators outside any BEGIN…END
 * block, so `scanDocument` fragments a CREATE PROCEDURE at the first
 * declaration `;` (verified) — a statement slice would drop earlier
 * declarations. Full source keeps parsing correct and offset translation an
 * identity (`offset - 0`); oracle still gains the shared cache and the D6
 * oversize guard. A real oracle extractor (per-`/`-batch slicing) is documented
 * follow-up. Exported so the slice unit test can pin the full-source contract.
 */
export function oracleStatementSlice(): { start: number; end: number } | null {
  return null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Minimal shape of a PL/SQL subprogram parameter this module reads. */
interface SubprogramParamLike {
  name: string;
  modes: readonly string[];
  typeText: string;
}

/** Minimal shape of a PL/SQL subprogram signature this module reads. */
interface SubprogramSigLike {
  parameters: readonly SubprogramParamLike[];
  declRange: { startOffset: number };
}

/** Pick the parameters of the subprogram that lexically encloses `offset`.
 *  PL/SQL subprograms nest (package bodies contain member procedures), so the
 *  enclosing subprogram is the one whose declaration STARTS latest at or
 *  before the cursor. Returns `[]` when no subprogram starts before the
 *  cursor (e.g. an anonymous `DECLARE … BEGIN` block, which has no params). */
function pickEnclosingSubprogramParams(
  offset: number,
  sigs: readonly SubprogramSigLike[],
): readonly SubprogramParamLike[] {
  let best: SubprogramSigLike | null = null;
  for (const sig of sigs) {
    if (sig.declRange.startOffset > offset) continue;
    if (!best || sig.declRange.startOffset > best.declRange.startOffset) {
      best = sig;
    }
  }
  return best ? best.parameters : [];
}

function skipCreateOrReplacePreamble(
  tokens: readonly Token[],
  idx: number,
): number {
  if (
    identUpper(tokens[idx]) === 'OR' &&
    identUpper(tokens[idx + 1]) === 'REPLACE'
  ) {
    return idx + 2;
  }
  return idx;
}

/**
 * Find the next `IS` or `AS` keyword token starting at `from`. Bounded to
 * the next 64 tokens to avoid scanning across unrelated statements on
 * malformed inputs.
 */
function findNextIsOrAsIdx(tokens: readonly Token[], from: number): number {
  const limit = Math.min(tokens.length, from + 64);
  for (let i = from; i < limit; i += 1) {
    const upper = identUpper(tokens[i]);
    if (upper === 'IS' || upper === 'AS') return i;
  }
  return -1;
}

function identUpper(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
}

// ─── text-scan fallback ──────────────────────────────────────────────────────

/** Words that open a declaration but are not themselves the declared name. */
const DECLARATION_LEADS: ReadonlySet<string> = new Set(['CURSOR', 'TYPE', 'SUBTYPE']);
/** Words that start a nested routine or a pragma rather than a variable. */
const DECLARATION_SKIP: ReadonlySet<string> = new Set([
  'PRAGMA', 'FUNCTION', 'PROCEDURE', 'BEGIN', 'END', 'IS', 'AS', 'DECLARE',
]);

/**
 * Names declared in the enclosing PL/SQL body, found by a pure text scan.
 *
 * The ANTLR path above is the source of truth, but it yields NOTHING when the
 * body does not parse — which is the normal state while the body is being
 * written (no `END;` typed yet). That left `v_total` and the routine's own
 * parameters missing from every caret in a half-written procedure, the moment
 * they are most wanted. T-SQL and MySQL already degrade this way; this brings
 * Oracle in line.
 *
 * Deliberately permissive: it collects from every `IS|AS|DECLARE … BEGIN`
 * region before the cursor rather than resolving exact visibility, so a sibling
 * routine's locals in a package body may leak in. For a fallback that only runs
 * when the parse already failed, over-offering beats offering nothing.
 */
export function scanOracleDeclaredNames(
  source: string,
  offset: number,
): readonly { readonly name: string; readonly detail: string }[] {
  const tokens = tokenize(source, 0, offset, ENGINE);
  const out: { name: string; detail: string }[] = [];
  const seen = new Set<string>();
  const add = (name: string, detail: string) => {
    const key = name.toLowerCase();
    if (seen.has(key) || DECLARATION_SKIP.has(name.toUpperCase())) return;
    seen.add(key);
    out.push({ name, detail });
  };

  for (let i = 0; i < tokens.length; i += 1) {
    const word = identUpper(tokens[i]);
    if (word === 'DECLARE' || ((word === 'IS' || word === 'AS') && opensRoutineBody(tokens, i))) {
      collectDeclarations(tokens, i + 1, add);
    }
    // A signature paren directly before the body introducer holds the formal
    // parameters: `PROCEDURE p(p_id IN NUMBER, p_name VARCHAR2) IS`.
    if (tokens[i]?.type === 'lparen' && isRoutineSignatureParen(tokens, i)) {
      collectParameters(tokens, i + 1, add);
    }
  }
  return out;
}

/** Reads `<name> <type>;` declarations until the region's `BEGIN`. */
function collectDeclarations(
  tokens: readonly Token[],
  from: number,
  add: (name: string, detail: string) => void,
): void {
  let atDeclarationStart = true;
  for (let i = from; i < tokens.length; i += 1) {
    const tok = tokens[i]!;
    const word = identUpper(tok);
    if (word === 'BEGIN') return;
    if (tok.type === 'semicolon') {
      atDeclarationStart = true;
      continue;
    }
    if (!atDeclarationStart || (tok.type !== 'ident' && tok.type !== 'kw')) continue;
    // `CURSOR c IS …` / `TYPE t IS …` — the NAME is the next word.
    if (word !== null && DECLARATION_LEADS.has(word)) {
      const next = tokens[i + 1];
      if (next?.type === 'ident') add(next.text, word === 'CURSOR' ? 'cursor' : 'type');
      atDeclarationStart = false;
      continue;
    }
    add(tok.text, 'local variable');
    atDeclarationStart = false;
  }
}

/** Reads `(<name> [IN|OUT] <type>, …)` formal parameters. */
function collectParameters(
  tokens: readonly Token[],
  from: number,
  add: (name: string, detail: string) => void,
): void {
  let atParamStart = true;
  let depth = 1;
  for (let i = from; i < tokens.length; i += 1) {
    const tok = tokens[i]!;
    if (tok.type === 'lparen') { depth += 1; continue; }
    if (tok.type === 'rparen') {
      depth -= 1;
      if (depth === 0) return;
      continue;
    }
    if (depth !== 1) continue;
    if (tok.type === 'comma') { atParamStart = true; continue; }
    if (atParamStart && tok.type === 'ident') {
      add(tok.text, 'parameter');
      atParamStart = false;
    }
  }
}

/** True when the `IS` / `AS` at `idx` opens a ROUTINE BODY rather than a cursor
 *  or type declaration (`CURSOR c IS SELECT …` has its own `IS`, and treating
 *  it as a body introducer read the following `SELECT` as a declared name). */
function opensRoutineBody(tokens: readonly Token[], idx: number): boolean {
  // `… PROCEDURE p (params) IS` — step back over the signature parens.
  let i = idx - 1;
  if (tokens[i]?.type === 'rparen') {
    let depth = 1;
    i -= 1;
    while (i >= 0 && depth > 0) {
      if (tokens[i]!.type === 'rparen') depth += 1;
      else if (tokens[i]!.type === 'lparen') depth -= 1;
      i -= 1;
    }
  }
  // …then look back over the routine's (possibly dotted, possibly schema-
  // qualified) name for the declaring keyword. A bounded window rather than a
  // greedy ident walk, because this tokenizer lexes PROCEDURE / FUNCTION as
  // plain identifiers.
  for (let k = i; k >= 0 && k > i - 6; k -= 1) {
    const word = identUpper(tokens[k]);
    if (word !== null && ROUTINE_DECLARERS.has(word)) return true;
  }
  return false;
}

/** Keywords that declare something with a PL/SQL body. */
const ROUTINE_DECLARERS: ReadonlySet<string> = new Set([
  'PROCEDURE', 'FUNCTION', 'PACKAGE', 'BODY', 'TRIGGER',
]);

/** True when the `(` at `idx` opens a routine signature — the word before it is
 *  an identifier whose own predecessor is PROCEDURE / FUNCTION. */
function isRoutineSignatureParen(tokens: readonly Token[], idx: number): boolean {
  const name = tokens[idx - 1];
  if (!name || (name.type !== 'ident' && name.type !== 'kw')) return false;
  const keyword = identUpper(tokens[idx - 2]);
  return keyword === 'PROCEDURE' || keyword === 'FUNCTION';
}
